// ---------------------------------------------------------------------------
// useProductSearch
//
// Provides product-search suggestions and product details for the planogram.
// Product results are normalized here so weight/volume is not discarded
// before reaching PlanogramProductCellFactory.
// ---------------------------------------------------------------------------

import {
    useCallback,
    useRef,
    useState,
} from 'react';

import {
    ProductSearchService,
} from '../services/ProductSearchService';

import {
    ProductOption,
    ProductSearchEvent,
} from '../domain/types';

type WeightOrVolume = {
    value: number;
    unit: string;
};

const parseNumber = (
    value: unknown,
): number | null => {
    if (typeof value === 'number') {
        return Number.isFinite(value)
            ? value
            : null;
    }

    if (typeof value !== 'string') {
        return null;
    }

    const normalized = value
        .trim()
        .replace(',', '.');

    if (!normalized) {
        return null;
    }

    const parsed = Number(normalized);

    return Number.isFinite(parsed)
        ? parsed
        : null;
};

const resolveWeightOrVolume = (
    mappedProduct: Record<string, any>,
    sourceProduct?: Record<string, any>,
): WeightOrVolume | null => {
    const structuredValue =
        mappedProduct?.weightOrVolume ??
        mappedProduct?.weight_or_volume ??
        sourceProduct?.weightOrVolume ??
        sourceProduct?.weight_or_volume ??
        null;

    const rawValue =
        structuredValue?.value ??
        mappedProduct?.weightVolume ??
        mappedProduct?.weight_volume ??
        sourceProduct?.weightVolume ??
        sourceProduct?.weight_volume ??
        null;

    const rawUnit =
        structuredValue?.unit ??
        mappedProduct?.unitOfMeasurement ??
        mappedProduct?.unit_of_measurement ??
        sourceProduct?.unitOfMeasurement ??
        sourceProduct?.unit_of_measurement ??
        null;

    const value = parseNumber(rawValue);
    const unit = String(rawUnit ?? '').trim();

    if (value === null || !unit) {
        return null;
    }

    return {
        value,
        unit,
    };
};

const normalizeProductOption = (
    mappedProduct: Record<string, any>,
    sourceProduct?: Record<string, any>,
): ProductOption => {
    const weightOrVolume =
        resolveWeightOrVolume(
            mappedProduct,
            sourceProduct,
        );

    return {
        ...mappedProduct,

        weightOrVolume,

        /*
         * Keep the legacy properties because current planogram UI
         * components still read them.
         */
        weightVolume:
            weightOrVolume?.value ??
            mappedProduct?.weightVolume ??
            mappedProduct?.weight_volume ??
            sourceProduct?.weightVolume ??
            sourceProduct?.weight_volume ??
            null,

        unitOfMeasurement:
            weightOrVolume?.unit ??
            mappedProduct?.unitOfMeasurement ??
            mappedProduct?.unit_of_measurement ??
            sourceProduct?.unitOfMeasurement ??
            sourceProduct?.unit_of_measurement ??
            null,
    } as ProductOption;
};

export function useProductSearch() {
    const [
        suggestions,
        setSuggestions,
    ] = useState<ProductOption[]>([]);

    const [
        loading,
        setLoading,
    ] = useState(false);

    const latestRequestRef =
        useRef(0);

    const search = useCallback(
        async (
            event: ProductSearchEvent,
        ) => {
            const query =
                event.query.trim();

            if (query.length < 2) {
                setSuggestions([]);
                return;
            }

            const requestId = Date.now();

            latestRequestRef.current =
                requestId;

            setLoading(true);

            try {
                const results =
                    await ProductSearchService.search(
                        query,
                    );

                if (
                    latestRequestRef.current !==
                    requestId
                ) {
                    return;
                }

                const normalizedResults =
                    (
                        Array.isArray(results)
                            ? results
                            : []
                    ).map(result =>
                        normalizeProductOption(
                            result as Record<
                                string,
                                any
                            >,
                        ),
                    );

                setSuggestions(
                    normalizedResults,
                );
            } finally {
                if (
                    latestRequestRef.current ===
                    requestId
                ) {
                    setLoading(false);
                }
            }
        },
        [],
    );

    const clear = useCallback(
        () => setSuggestions([]),
        [],
    );

    const getDetailsByExternalId =
        useCallback(
            (
                externalId: string,
            ) =>
                ProductSearchService
                    .getDetailsByExternalId(
                        externalId,
                    ),
            [],
        );

    const mapProductToOption =
        useCallback(
            (
                product: Record<
                    string,
                    unknown
                >,
            ): ProductOption | null => {
                const mappedProduct =
                    ProductSearchService
                        .mapProductToOption(
                            product,
                        );

                if (!mappedProduct) {
                    return null;
                }

                return normalizeProductOption(
                    mappedProduct as Record<
                        string,
                        any
                    >,
                    product as Record<
                        string,
                        any
                    >,
                );
            },
            [],
        );

    return {
        suggestions,
        loading,
        search,
        clear,
        getDetailsByExternalId,
        mapProductToOption,
    };
}

export type UseProductSearchReturn =
    ReturnType<typeof useProductSearch>;